import { useEffect, useMemo, useState } from 'react'
import { Trans } from '@lingui/macro'

import { Typography, Stack, Paper, Grid } from '@mui/material'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'

import { useGetPlanningsQuery } from '@/core/services/planning'
import { useGetEstablishmentQuery } from '@/core/services/establishment'
import { Planning } from '@/core/models/planning.model'
import { useQueryParam } from '@/hooks/useQueryParam'

import { CreatePlanningDialog } from '@/shared/plannings'
import { selectDefaultPlanning } from '@/shared/plannings/lib/planningSelection'

import { PlanningDetails } from './PlanningDetails'
import { PlanningViewMode } from '../model/types'
import { Sidebar } from './Sidebar'

type EstablishmentPlanningsManagementProps = {
  establishmentId: string
}

export function EstablishmentPlanningsManagement({ establishmentId }: EstablishmentPlanningsManagementProps) {
  const [planningId, setPlanningId] = useQueryParam('planningId', (val) => val ?? '')
  const [planningViewMode, setPlanningViewMode] = useState<PlanningViewMode>('all')

  const { data: establishment } = useGetEstablishmentQuery(establishmentId)

  const { data: plannings = [] } = useGetPlanningsQuery({ visibility: 'facility', facilityId: establishmentId })

  const selectedPlanning = useMemo(() => selectDefaultPlanning(plannings, planningId || undefined), [planningId, plannings])

  useEffect(() => {
    if (selectedPlanning && planningId !== selectedPlanning._id) {
      setPlanningId(selectedPlanning._id)
    }
  }, [planningId, selectedPlanning, setPlanningId])

  if (!establishment) return null

  return (
    <Stack height="100%">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={{ xs: 1, md: 2 }}>
        <Typography variant="h5" component="h1" sx={{ display: { xs: 'none', md: 'block' } }}>
          <Trans>Plannings Management - {establishment.name}</Trans>
        </Typography>
        <CreatePlanningDialog visibility="facility" visibilityId={establishmentId} />
      </Stack>

      <Grid container spacing={{ xs: 1, md: 2 }}>
        <Grid item xs={12} md={4} xl={3}>
          <Sidebar
            plannings={plannings}
            planningViewMode={planningViewMode}
            setPlanningViewMode={setPlanningViewMode}
            setPlanningId={setPlanningId}
            selectedPlanning={selectedPlanning}
          />
        </Grid>
        <Grid item xs={12} md={8} xl={9}>
          <MainContent selectedPlanning={selectedPlanning} planningViewMode={planningViewMode} />
        </Grid>
      </Grid>
    </Stack>
  )
}

function MainContent({ planningViewMode, selectedPlanning }: { planningViewMode: PlanningViewMode; selectedPlanning?: Planning }) {
  if (!selectedPlanning) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <AdminPanelSettingsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          <Trans>No Planning Selected</Trans>
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          <Trans>Please select a planning from the list or create a new one.</Trans>
        </Typography>
      </Paper>
    )
  }

  return <PlanningDetails planning={selectedPlanning} viewMode={planningViewMode} />
}
